/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import elementResizeDetector from 'element-resize-detector';

import DomEventsManager from 'scripts/dom-events-manager';
import appContext from 'scripts/app-context';
import dtm from 'scripts/services/dtm';

import { hideTooltip } from '../tooltipHelpers';

import template from './round.stache';

const pageType = window.globalSettings.pageType;

const HOLES_COUNT = 18;

can.Component.extend({
    tag: 'probability-round',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            round: {
                type: 'number',
                value: 1,
            },
            showVideo: {
                type: 'boolean',
                value: false,
            },
            tooltipText: {
                type: 'string',
                value: '',
            },
            pageType: {
                type: 'string',
            },
            player: {
                Type: can.Map,
            },
            activeVideo: {
                Type: can.Map,
                value: null,
            },
            chartData: {
                type: 'array',
                value: [],
                set(value) {
                    const playerPointsLength = _.get(
                        value,
                        'points.playerPoints.length',
                    );

                    this.attr('noData', !playerPointsLength);

                    return value || [];
                },
            },
            refreshKey: {
                type: 'number',
                value: 0,
            },
            showLeaders: {
                type: 'boolean',
            },
            activeTooltipId: {
                type: 'string',
                value: null,
            },
        },

        isActive(video) {
            const activeVideo = this.attr('activeVideo');
            if (activeVideo) {
                return activeVideo.identifier === video.identifier;
            }
            return false;
        },

        analyticsVideoSource() {
            switch (pageType) {
                case 'player-detail':
                    return 'Player Detail Recap';
                case 'scoring':
                    return 'Leader Board Recap';
                default:
                    return '';
            }
        },

        play(video, hole) {
            let round = this.round;
            let player = this.player;
            let title =
                video.title ||
                `${player.firstName} ${
                    player.lastName
                }: Hole ${hole}, Round ${round}`;

            // set current round & title
            video.attr('title', title);
            video.attr('round', round);
            video.attr('hole', hole);

            this.attr('activeVideo', video);
            dtm.trackVideoLaunch(video.title, this.analyticsVideoSource());
        },

        getVideoButtonLeft(holeNumber) {
            return (this.adaptHoleNumber(holeNumber) / HOLES_COUNT) * 100;
        },

        adaptHoleNumber(holeNumber) {
            if (this.attr('startHole') >= 10) {
                const offsetIndex = 9;
                if (holeNumber === offsetIndex) {
                    return HOLES_COUNT;
                }

                return (holeNumber + offsetIndex) % HOLES_COUNT;
            }
            return holeNumber;
        },
        hideTooltip() {
            const tooltip = this.attr('tooltip');
            hideTooltip(tooltip);
        },
        onPlayClick(_context, _el, _event, hole, video) {
            this.play(video, hole);
        },
    },
    events: {
        inserted() {
            DomEventsManager.subscribe(this);
            this.registerModelListeners();
            this.registerEventListeners();
        },

        registerEventListeners() {
            can.$(window).on('scroll', () => this.viewModel.hideTooltip());
            can.$(window).on('touchmove', () => this.viewModel.hideTooltip());
        },

        rerenderChart() {
            this.viewModel.attr('refreshKey', Math.random());
        },

        registerModelListeners() {
            const resizeDetector = elementResizeDetector();
            resizeDetector.listenTo(
                this.element.find('.c-probability-chart'),
                () => {
                    this.rerenderChart();
                },
            );

            appContext.model.bind('drawer-content-tabs-toggle', (__, type) => {
                if (this.element && type === 'stats') {
                    this.rerenderChart();
                }
            });

            this.viewModel.bind('showLeaders', () => {
                this.rerenderChart();
            });

            this.viewModel.bind('tooltip', () => {
                this.rerenderChart();
            });
        },

        removed: function() {
            DomEventsManager.unsubscribe(this);
        },

        onLayout(layout) {
            if (this.layout && this.layout !== layout) {
                this.rerenderChart();
            }
            this.layout = layout;
        },
    },
});
